import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  DynamicField,
  DynamicCase,
  ApiKeyStatus,
  CreateSessionResponse,
  CaseProcessingRequest,
  CaseProcessingResponse,
  ProcessingStatus,
  CreateCaseRequest,
  UpdateCaseRequest,
  CreateFieldRequest,
  UpdateFieldRequest,
  CaseAttachment,
  AvatarSessionResponse
} from '../../types';
import type { 
  GetCasesArgs,
  CreateSessionArgs,
  SubmitDiagnosisArgs,
  AskQuestionArgs 
} from '../types';

// Define base query
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:3001',
  credentials: 'include',
  prepareHeaders: (headers) => {
    headers.set('Content-Type', 'application/json');
    return headers;
  },
});

// Create API slice with updated types
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery,
  
  // Define tag types for caching
  tagTypes: ['Field', 'Case', 'Session', 'ApiStatus', 'Config', 'Processing', 'Attachment'],
  
  endpoints: (builder) => ({
    // ================================
    // DYNAMIC FIELDS ENDPOINTS
    // ================================
    
    // Get all dynamic fields
    getFields: builder.query<DynamicField[], void>({
      query: () => '/api/fields',
      transformResponse: (response: { data: DynamicField[] }) => response.data,
      providesTags: ['Field'],
    }),
    
    // Get specific field
    getField: builder.query<DynamicField, string>({
      query: (fieldId) => `/api/fields/${fieldId}`,
      transformResponse: (response: { data: DynamicField }) => response.data,
      providesTags: (result, error, fieldId) => [{ type: 'Field', id: fieldId }],
    }),
    
    // Create new field
    createField: builder.mutation<DynamicField, CreateFieldRequest>({
      query: (fieldData) => ({
        url: '/api/fields',
        method: 'POST',
        body: fieldData,
      }),
      transformResponse: (response: { data: DynamicField }) => response.data,
      invalidatesTags: ['Field'],
    }),
    
    // Update field
    updateField: builder.mutation<DynamicField, { fieldId: string; updates: UpdateFieldRequest }>({
      query: ({ fieldId, updates }) => ({
        url: `/api/fields/${fieldId}`,
        method: 'PUT',
        body: updates,
      }),
      transformResponse: (response: { data: DynamicField }) => response.data,
      invalidatesTags: (result, error, { fieldId }) => [
        { type: 'Field', id: fieldId },
        'Field'
      ],
    }),
    
    // Delete field
    deleteField: builder.mutation<void, string>({
      query: (fieldId) => ({
        url: `/api/fields/${fieldId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, fieldId) => [
        { type: 'Field', id: fieldId },
        'Field'
      ],
    }),

    // ================================
    // DYNAMIC CASES ENDPOINTS
    // ================================
    
    // Get cases for a specific field
    getCases: builder.query<DynamicCase[], GetCasesArgs>({
      query: ({ fieldId }) => `/api/cases?fieldId=${fieldId}`,
      transformResponse: (response: { data: DynamicCase[] }) => response.data,
      providesTags: (result, error, { fieldId }) => [
        { type: 'Case', id: fieldId },
        ...((result || []).map(({ id }) => ({ type: 'Case' as const, id })))
      ],
    }),
    
    // Get specific case
    getCase: builder.query<DynamicCase, string>({
      query: (caseId) => `/api/cases/${caseId}`,
      transformResponse: (response: { data: DynamicCase }) => response.data,
      providesTags: (result, error, caseId) => [{ type: 'Case', id: caseId }],
    }),
    
    // Create case manually
    createCase: builder.mutation<DynamicCase, CreateCaseRequest>({
      query: (caseData) => ({
        url: '/api/cases',
        method: 'POST',
        body: caseData,
      }),
      transformResponse: (response: { data: DynamicCase }) => response.data,
      invalidatesTags: (result, error, { fieldId }) => [
        { type: 'Case', id: fieldId },
        'Case'
      ],
    }),
    
    // Update case
    updateCase: builder.mutation<DynamicCase, { caseId: string; updates: UpdateCaseRequest }>({
      query: ({ caseId, updates }) => ({
        url: `/api/cases/${caseId}`,
        method: 'PUT',
        body: updates,
      }),
      transformResponse: (response: { data: DynamicCase }) => response.data,
      invalidatesTags: (result, error, { caseId }) => [
        { type: 'Case', id: caseId },
        'Case'
      ],
    }),
    
    // Delete case
    deleteCase: builder.mutation<void, string>({
      query: (caseId) => ({
        url: `/api/cases/${caseId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, caseId) => [
        { type: 'Case', id: caseId },
        'Case'
      ],
    }),

    // ================================
    // LLM PROCESSING ENDPOINTS (NEW)
    // ================================
    
    // Process case using LLM
    processCase: builder.mutation<CaseProcessingResponse, CaseProcessingRequest>({
      query: (processingData) => ({
        url: '/api/cases/process',
        method: 'POST',
        body: processingData,
      }),
      transformResponse: (response: { data: CaseProcessingResponse }) => response.data,
      invalidatesTags: ['Processing'],
    }),
    
    // Get processing status
    getProcessingStatus: builder.query<ProcessingStatus, string>({
      query: (processingId) => `/api/cases/processing/${processingId}`,
      transformResponse: (response: { data: ProcessingStatus }) => response.data,
      providesTags: (result, error, processingId) => [
        { type: 'Processing', id: processingId }
      ],
    }),
    
    // Search cases
    searchCases: builder.query<DynamicCase[], { searchTerm: string; fieldId?: string }>({
      query: ({ searchTerm, fieldId }) => {
        const params = new URLSearchParams({ q: searchTerm });
        if (fieldId) params.append('fieldId', fieldId);
        return `/api/cases/search?${params.toString()}`;
      },
      transformResponse: (response: { data: DynamicCase[] }) => response.data,
      providesTags: ['Case'],
    }),
    
    // Get cases by status
    getCasesByStatus: builder.query<DynamicCase[], { 
      status: DynamicCase['processing_status']; 
      fieldId?: string;
    }>({
      query: ({ status, fieldId }) => {
        const params = new URLSearchParams({ status });
        if (fieldId) params.append('fieldId', fieldId);
        return `/api/cases/by-status?${params.toString()}`;
      },
      transformResponse: (response: { data: DynamicCase[] }) => response.data,
      providesTags: ['Case'],
    }),
    
    // Get case statistics
    getCaseStatistics: builder.query<any, { fieldId?: string }>({
      query: ({ fieldId }) => {
        const params = fieldId ? `?fieldId=${fieldId}` : '';
        return `/api/cases/statistics${params}`;
      },
      transformResponse: (response: { data: any }) => response.data,
      providesTags: ['Case'],
    }),

    // ================================
    // SESSION ENDPOINTS (UPDATED)
    // ================================
    
    // Create new session
    createSession: builder.mutation<CreateSessionResponse, CreateSessionArgs>({
      query: (args) => ({
        url: '/api/sessions',
        method: 'POST',
        body: args,
      }),
      transformResponse: (response: { data: CreateSessionResponse }) => response.data,
      invalidatesTags: ['Session'],
    }),
    
    // Get session details
    getSession: builder.query<any, string>({
      query: (sessionId) => `/api/sessions/${sessionId}`,
      transformResponse: (response: { data: any }) => response.data,
      providesTags: (result, error, sessionId) => [{ type: 'Session', id: sessionId }],
    }),
    
    // End session
    endSession: builder.mutation<void, string>({
      query: (sessionId) => ({
        url: `/api/sessions/${sessionId}/end`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, sessionId) => [{ type: 'Session', id: sessionId }],
    }),

    // ================================
    // CONFIGURATION ENDPOINTS (UNCHANGED)
    // ================================
    
    // Get API key status
    getApiKeyStatus: builder.query<ApiKeyStatus, void>({
      query: () => '/api/config/api-keys',
      transformResponse: (response: { data: ApiKeyStatus }) => response.data,
      providesTags: ['ApiStatus'],
    }),
    
    // Check avatar provider configuration
    checkAvatarConfig: builder.query<{ configured: boolean }, void>({
      query: () => '/api/config/avatar-key',
      transformResponse: (response: { data: { configured: boolean } }) => response.data,
      providesTags: ['Config'],
    }),
    
    // Create the selected avatar provider session
    createAvatarSession: builder.mutation<
      AvatarSessionResponse,
      { conversationName?: string; caseId?: string; provider?: "anam" | "tavus" } | void
    >({
      query: (body) => ({
        url: '/api/avatar/session',
        method: 'POST',
        body: body || {},
      }),
      transformResponse: (response: { data: AvatarSessionResponse }) => response.data,
    }),
    
    // Get Deepgram config
    getDeepgramConfig: builder.query<{ proxyAvailable: boolean; wsUrl: string }, void>({
      query: () => '/api/config/deepgram-config',
      transformResponse: (response: { data: { proxyAvailable: boolean; wsUrl: string } }) => response.data,
      providesTags: ['Config'],
    }),
    
    // Get feature flags
    getFeatures: builder.query<Record<string, boolean>, void>({
      query: () => '/api/config/features',
      transformResponse: (response: { data: Record<string, boolean> }) => response.data,
      providesTags: ['Config'],
    }),

    // ================================
    // ATTACHMENT ENDPOINTS
    // ================================

    // Get attachments for a case (with presigned download URLs)
    getCaseAttachments: builder.query<CaseAttachment[], string>({
      query: (caseId) => `/api/cases/${caseId}/attachments`,
      transformResponse: (response: { data: CaseAttachment[] }) => response.data,
      providesTags: (result, error, caseId) => [{ type: 'Attachment', id: caseId }],
    }),

    // Get presigned upload URL
    getUploadUrl: builder.mutation<
      { uploadUrl: string; s3Key: string },
      { caseId: string; fileName: string; mimeType: string; fileSize: number }
    >({
      query: ({ caseId, ...body }) => ({
        url: `/api/cases/${caseId}/attachments/upload-url`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: { uploadUrl: string; s3Key: string } }) => response.data,
    }),

    // Confirm upload after successful S3 PUT
    confirmUpload: builder.mutation<
      CaseAttachment,
      {
        caseId: string;
        s3Key: string;
        fileName: string;
        originalName: string;
        mimeType: string;
        fileSize: number;
      }
    >({
      query: ({ caseId, ...body }) => ({
        url: `/api/cases/${caseId}/attachments/confirm`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: CaseAttachment }) => response.data,
      invalidatesTags: (result, error, { caseId }) => [{ type: 'Attachment', id: caseId }],
    }),

    // Delete attachment (admin only)
    deleteCaseAttachment: builder.mutation<void, { caseId: string; attachmentId: string }>({
      query: ({ caseId, attachmentId }) => ({
        url: `/api/cases/${caseId}/attachments/${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { caseId }) => [{ type: 'Attachment', id: caseId }],
    }),
  }),
});

// Export hooks for usage in components
export const {
  // Field hooks
  useGetFieldsQuery,
  useGetFieldQuery,
  useCreateFieldMutation,
  useUpdateFieldMutation,
  useDeleteFieldMutation,
  
  // Case hooks
  useGetCasesQuery,
  useGetCaseQuery,
  useCreateCaseMutation,
  useUpdateCaseMutation,
  useDeleteCaseMutation,
  
  // Processing hooks (NEW)
  useProcessCaseMutation,
  useGetProcessingStatusQuery,
  useSearchCasesQuery,
  useGetCasesByStatusQuery,
  useGetCaseStatisticsQuery,
  
  // Session hooks
  useCreateSessionMutation,
  useGetSessionQuery,
  useEndSessionMutation,
  
  // Config hooks
  useGetApiKeyStatusQuery,
  useCheckAvatarConfigQuery,
  useCreateAvatarSessionMutation,
  useGetDeepgramConfigQuery,
  useGetFeaturesQuery,

  // Attachment hooks
  useGetCaseAttachmentsQuery,
  useGetUploadUrlMutation,
  useConfirmUploadMutation,
  useDeleteCaseAttachmentMutation,

  // Lazy versions
  useLazyGetCasesQuery,
  useLazyGetCaseQuery,
  useLazyGetSessionQuery,
  useLazySearchCasesQuery,
  useLazyGetProcessingStatusQuery,
} = apiSlice;

// Export endpoints for use in middleware
export const {
  getFields,
  getCases,
  getCase,
  createCase,
  updateCase,
  deleteCase,
  processCase,
  getProcessingStatus,
  searchCases,
  getCasesByStatus,
  getCaseStatistics,
  createSession,
  getSession,
  endSession,
  getApiKeyStatus,
  checkAvatarConfig,
  createAvatarSession,
  getDeepgramConfig,
  getFeatures,
} = apiSlice.endpoints;
